import { useEffect, useMemo } from 'react';
import { createShadowBlobTexture } from './materials/shadowBlob';
import type { CarBounds, Size2Tuple, Vector3Tuple } from '../types';

/**
 * 바닥(y = -0.002)보다 위, 타이어 접지면(y = 0)보다 아래.
 * 위로 올리면 타이어 밑동을 자르는 이음선이 생기고, 바닥과 같으면 z-fighting이 납니다.
 */
const SHADOW_Y = -0.001;

/**
 * 차량 실루엣보다 조금 넓게. 그림자가 차체 폭과 정확히 같으면 판의 경계가
 * 차체 윤곽과 겹쳐 보여서 오히려 스티커처럼 읽힙니다.
 */
const SPREAD = 1.18;

export interface DrivingShadowProps {
  bounds: CarBounds;
  /** 배경 프리셋의 접지 그림자 농도. 스튜디오마다 조명이 달라 그대로 따릅니다. */
  opacity: number;
}

/**
 * 주행 중 차량과 함께 움직이는 접지 그림자.
 *
 * 차량 그룹 안에 있지만 **차체 기울기 그룹 밖**에 두어야 합니다. 바닥에 드리운
 * 그림자는 차가 롤·피치로 기울어도 함께 기울지 않습니다.
 */
export function DrivingShadow({ bounds, opacity }: DrivingShadowProps) {
  const texture = useMemo(() => createShadowBlobTexture(), []);

  // 우리가 만든 텍스처라 R3F가 정리해 주지 않습니다. 모드를 여러 번 드나들면 쌓입니다.
  useEffect(() => () => texture?.dispose(), [texture]);

  const { position, size } = useMemo(() => {
    const length = bounds.frontX - bounds.rearX;
    return {
      /*
       * 차량 앞뒤가 원점 기준으로 대칭이 아닌 에셋이 있습니다(M8: rearX -2.668,
       * frontX 2.246). 그림자를 원점에 두면 한쪽으로 치우칩니다.
       */
      position: [(bounds.frontX + bounds.rearX) / 2, SHADOW_Y, 0] as Vector3Tuple,
      size: [length * SPREAD, bounds.halfWidth * 2 * SPREAD] as Size2Tuple,
    };
  }, [bounds]);

  if (texture === null) {
    return null;
  }

  return (
    <mesh position={position} rotation={[-Math.PI / 2, 0, 0]}>
      {/* -90° 회전 뒤 판의 두 번째 변이 월드 Z를 덮습니다. args는 [전후, 좌우] 순입니다. */}
      <planeGeometry args={size} />
      {/*
        depthWrite를 끄는 이유: 반투명 판이 깊이 버퍼에 쓰면 뒤에 그려지는 차량 하부가
        그림자에 가려집니다. 조명을 받을 필요도 없어 Basic 머티리얼을 씁니다.
      */}
      <meshBasicMaterial map={texture} transparent opacity={opacity} depthWrite={false} />
    </mesh>
  );
}
